import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import {
  getAddress,
  getAllAddress,
  IAddress,
  updateAddressStatus,
} from "./AddressViewController";
import { IPagination } from "../../data/AppInterface";
import {
  approveIcon,
  filterIcon,
  galleryImageIcon,
  infoIcon,
  pdfIcon,
  rejectIcon,
} from "../../assets/icons/SvgIconList";
import { ADDRESS_STATUS_OPTION, ORDER_BY } from "../../utils/AppConstants";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICImage,
  ICModal,
  ICTable,
  PdfViewer,
} from "../../core-component/ui";
import { ADDRESS_STATUS, FILE_TYPE } from "../../utils/AppEnumerations";
import { ICTextArea, ICDropDown } from "../../core-component/form";
import { prepareMessageFromParams } from "../../utils/AppFunctions";

let timer: ReturnType<typeof setTimeout>;

const AddressView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [addressList, setAddressList] = useState<IAddress[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [showVerifiedConfirmation, setShowVerifiedConfirmation] =
    useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [addressToView, setAddressToView] = useState<IAddress>();
  const [rejectReason, setRejectReason] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowProofModal, setIsShowProofModal] = useState(false);
  const [isShowProofPdfModal, setIsShowProofPdfModal] = useState(false);
  const [error, setError] = useState("");
  const [advanceSearch, setAdvanceSearch] = useState<string>("");

  useEffect(() => {
    fetchAddress({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  const fetchAddress = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllAddress(
        setAddressList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleStatusUpdate = async (
    value: boolean,
    status: ADDRESS_STATUS,
    reasons?: string,
  ) => {
    if (value && addressToView) {
      const success = await updateAddressStatus(
        addressToView.id,
        status,
        reasons,
      );
      if (success) {
        fetchAddress(pagination);
      }
    }
    setShowVerifiedConfirmation(false);
    setShowRejectConfirmation(false);
  };

  const filterView = (
    <div className="w-full flex">
      <ICDropDown
        className="min-w-72"
        placeholder={t("Status")}
        selected={advanceSearch}
        options={ADDRESS_STATUS_OPTION}
        onSelect={(option) => setAdvanceSearch(option.data_value)}
      />

      <div className="!py-0 ml-4">
        <ICButton onClick={() => setAdvanceSearch("")} className="flex !w-fit">
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );
  const getFileIcon = (fileUrl: string) => {
    if (!fileUrl) return null;
    const fileExtension = fileUrl.split(".").pop();
    return fileExtension ? fileExtension.toLowerCase() : null;
  };
  const customActionView = (item: IAddress) => {
    return (
      <div className="flex item-center justify-center w-full">
        {item.address_proof_url && (
          <div
            className="size-5 cursor-pointer"
            title={t("view_tooltip_icon")}
            onClick={() => {
              getFileIcon(item.address_proof_url) === FILE_TYPE.Pdf
                ? setIsShowProofPdfModal(true)
                : setIsShowProofModal(true);
              setAddressToView(item);
            }}
          >
            {getFileIcon(item.address_proof_url) === FILE_TYPE.Pdf
              ? pdfIcon
              : galleryImageIcon}
          </div>
        )}
        {item.status === ADDRESS_STATUS.Pending && (
          <>
            <div
              className="ml-2 size-5 cursor-pointer text-success"
              title={t("approve_tooltip_icon")}
              onClick={() => {
                setShowVerifiedConfirmation(true);
                setAddressToView(item);
              }}
            >
              {approveIcon}
            </div>
            <div
              className="ml-2 size-5 cursor-pointer text-danger"
              title={t("reject_tooltip_icon")}
              onClick={() => {
                setShowRejectConfirmation(true);
                setAddressToView(item);
              }}
            >
              {rejectIcon}
            </div>
          </>
        )}
        {item.status === ADDRESS_STATUS.Rejected && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("reject_view_tooltip_icon")}
            onClick={() => {
              setIsShowModal(true);
              setAddressToView(item);
            }}
          >
            {infoIcon}
          </div>
        )}
      </div>
    );
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      setError(t("rejected_required_massage"));
    } else {
      setError("");
      handleStatusUpdate(true, ADDRESS_STATUS.Rejected, rejectReason);
    }
  };

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchAddress(pagination);
        }}
        title={t("firm_address")}
      />

      <ICTable<IAddress>
        isLoading={isLoading}
        totalFlex="grid grid-cols-10"
        fields={getAddress(customActionView, t)}
        data={addressList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchAddress({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchAddress({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchAddress({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showVerifiedConfirmation && addressToView && (
        <ICConfirmationModal
          title={t("verify_address")}
          message={prepareMessageFromParams(t("verify_massage"), [
            ["firm", addressToView.firm_name],
          ])}
          onClose={(value) => {
            handleStatusUpdate(value, ADDRESS_STATUS.Verified);
          }}
        />
      )}
      {showRejectConfirmation && addressToView && (
        <ICModal
          onClose={() => {
            setShowRejectConfirmation(false);
          }}
          title={t("rejected_address")}
        >
          <div className="flex-1 overflow-y-scroll">
            <div>
              <ICTextArea
                type="text"
                label={t("rejected_reason")}
                containerClassName="mt-2"
                value={addressToView.reason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              {error && <p className="text-danger mt-1">{error}</p>}
            </div>
            <div className="flex w-full justify-center !py-2  space-x-5">
              <ICButton className="w-20" onClick={handleReject}>
                {t("yes")}
              </ICButton>
              <ICButton
                className="!bg-danger-muted !text-danger w-20"
                onClick={() => setShowRejectConfirmation(false)}
              >
                {t("no")}
              </ICButton>
            </div>
          </div>
        </ICModal>
      )}
      {isShowModal && addressToView && (
        <ICModal
          title={t("rejected_reason")}
          onClose={() => setIsShowModal(false)}
          children={addressToView.reason}
        />
      )}
      {isShowProofModal && addressToView && (
        <ICModal
          title={t("address_proof")}
          onClose={() => setIsShowProofModal(false)}
          children={
            <ICImage
              containerClass="bg-subtle flex-1 max-h-[calc(80vh-32px)] aspect-square"
              imageUrl={addressToView.address_proof_url}
              className="w-full h-full aspect-square object-contain"
            />
          }
        />
      )}
      {isShowProofPdfModal && addressToView && (
        <PdfViewer
          title={addressToView.firm_name}
          onClose={() => setIsShowProofPdfModal(false)}
          pdfFile={addressToView.address_proof_url}
        />
      )}
    </div>
  );
};

export default AddressView;
